import Icon from '../../../components/AppIcon';

const WorkflowTimeline = ({ stages, onStageClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success border-success';
      case 'active':
        return 'bg-primary border-primary';
      case 'pending':
        return 'bg-muted border-muted';
      case 'delayed':
        return 'bg-error border-error';
      default:
        return 'bg-muted border-muted';
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary':
        return 'bg-primary/10 text-primary';
      case 'success':
        return 'bg-success/10 text-success';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-error/10 text-error';
      case 'accent':
        return 'bg-accent/10 text-accent';
      case 'secondary':
        return 'bg-secondary/10 text-secondary';
      default:
        return 'bg-primary/10 text-primary';
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

        {/* Timeline Items */}
        <div className="space-y-8">
          {stages?.map((stage, index) => (
            <div
              key={stage?.id}
              className="relative flex items-start gap-6 cursor-pointer group"
              onClick={() => onStageClick(stage)}
            >
              {/* Timeline Dot */}
              <div className="relative z-10">
                <div className={`w-16 h-16 rounded-full border-4 ${getStatusColor(stage?.status)} flex items-center justify-center`}>
                  <Icon name={stage?.icon} size={24} className="text-white" />
                </div>
                {stage?.status === 'active' && (
                  <div className="absolute -inset-1 rounded-full border-2 border-primary animate-ping opacity-50"></div>
                )}
              </div>

              {/* Content Card */}
              <div className="flex-1 bg-card border border-border rounded-lg p-6 group-hover:shadow-lg transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                        {stage?.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getColorClasses(stage?.color)}`}>
                        Stage {index + 1}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>{stage?.timing}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    stage?.status === 'completed' ? 'bg-success/10 text-success' :
                    stage?.status === 'active' ? 'bg-primary/10 text-primary' :
                    stage?.status === 'delayed'? 'bg-error/10 text-error' : 'bg-muted/10 text-muted-foreground'
                  }`}>
                    {stage?.status?.charAt(0)?.toUpperCase() + stage?.status?.slice(1)}
                  </span>
                </div>

                {/* Progress Bar for Active Stages */}
                {stage?.status === 'active' && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Progress</span>
                      <span>{stage?.progress}%</span>
                    </div>
                    <div className="w-full bg-muted/20 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${stage?.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Objectives */}
                <p className="text-sm text-muted-foreground mb-4">
                  {stage?.objectives}
                </p>

                {/* Key Information Grid */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Contacts</p>
                    <div className="flex items-center gap-1">
                      <Icon name="Users" size={14} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {stage?.contacts?.length + stage?.backupContacts?.length}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Outputs</p>
                    <div className="flex items-center gap-1">
                      <Icon name="CheckSquare" size={14} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {stage?.requiredOutputs?.length}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Timeline</p>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} className="text-primary" />
                      <span className="text-xs font-medium text-foreground">
                        {stage?.completedDate
                          ? new Date(stage?.completedDate)?.toLocaleDateString()
                          : stage?.expectedCompletion
                          ? new Date(stage?.expectedCompletion)?.toLocaleDateString()
                          : stage?.expectedStart
                          ? new Date(stage?.expectedStart)?.toLocaleDateString()
                          : 'Not scheduled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Primary Contact Preview */}
                {stage?.contacts?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Primary Contact</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{stage?.contacts?.[0]?.name}</p>
                        <p className="text-xs text-muted-foreground">{stage?.contacts?.[0]?.role}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkflowTimeline;